import { NATIVE_TOKEN_ADDRESS, PLATFORM_NAME_TO_ENUM, PLATFORM_CONFIGS } from "../constants.js";
import { getPlatformFromPlatformId } from "../utils/format_utils.js";
import { validateAndFormatAddress, validateAndLowerCase } from "../utils/validateAndParseAddress.js";
import { FungibleTokenAmount } from "../models/financial/fungible_token_amount.js";
import { FungibleToken } from "../models/financial/fungible_token.js";
import { CurrencyAmount } from "../models/financial/currency_amount.js";
import { Currency } from "../models/financial/currency.js";
import _ from "lodash";
import { Buffer } from "buffer";
export const timer = (ms) => new Promise((res) => setTimeout(res, ms));
// used to filter out null and undefined values: https://stackoverflow.com/questions/43118692/typescript-filter-out-nulls-from-an-array
export const notEmpty = (value) => value != undefined && value != null;
/**
 * Gets the virtual machine type (EVM, SVM, etc.) from a platform ID
 * @param platformId The platform ID
 * @returns The VM type for the platform, defaults to EVM if not found
 */
export const getVirtualEnvironment = (platformId) => {
    const platform = getPlatformFromPlatformId(platformId);
    if (platform && PLATFORM_CONFIGS[platform]) {
        return PLATFORM_CONFIGS[platform].vmType;
    }
    return undefined;
};
/**
 * Gets the platform environment (mainnet/testnet) from a platform ID
 */
export const getPlatformEnvironment = (platformId) => {
    const platform = getPlatformFromPlatformId(platformId);
    if (platform && PLATFORM_CONFIGS[platform]) {
        return PLATFORM_CONFIGS[platform].environment;
    }
    return undefined;
};
export const validateValue = (value, name) => {
    if (!notEmpty(value))
        throw new Error(`${name} should be set`);
};
export const validateNonEmptyJSON = (value, name) => {
    if (value === null ||
        value === undefined ||
        (typeof value === "object" && _.isEmpty(value)) ||
        (typeof value === "string" && value.trim() === "")) {
        throw new Error(`${name} should be non-empty`);
    }
};
export const normalize = (value) => (notEmpty(value) ? value : null);
export const compareCustom = (value1, value2) => _.isEqual(normalize(value1), normalize(value2));
export const bigIntMax = (...args) => args.reduce((m, e) => (e > m ? e : m));
export const bigIntMin = (...args) => args.reduce((m, e) => (e < m ? e : m));
export const bigIntAbs = (value) => (value < BigInt(0) ? -value : value);
/**
 * Mesh Connect address encoding.
 * Convention: "{brokerType}:{accountId}" (e.g. "coinbase:abc123", "kraken:xyz789").
 */
const MESH_ADDRESS_SEPARATOR = ":";
export function createMeshAddress(brokerType, accountId) {
    return `${validateAndLowerCase(brokerType.trim())}${MESH_ADDRESS_SEPARATOR}${validateAndLowerCase(accountId.trim())}`;
}
export function parseMeshAddress(address) {
    const sep = address.indexOf(MESH_ADDRESS_SEPARATOR);
    if (sep <= 0)
        return undefined;
    return { brokerType: address.slice(0, sep), accountId: address.slice(sep + 1) };
}
export function toBigInt(v, options) {
    try {
        return BigInt(String(v ?? "0"));
    }
    catch {
        if (options?.strict) {
            return undefined;
        }
        return options?.fallback ?? BigInt(0);
    }
}
// this was copied from https://github.com/GoogleChromeLabs/jsbi/issues/30
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore: Unreachable code error
BigInt.prototype.toJSON = function () {
    return this.toString();
};
/**
 * @description sleeps for specified ms
 * @param {number} ms
 */
export const sleep = (ms = 10000) => {
    return new Promise((res, _rej) => setTimeout(res, ms));
};
export const isValidURL = async (url) => {
    try {
        const response = await fetch(url);
        return response.status == 200;
    }
    catch {
        return false;
    }
};
export const formatString = (string) => {
    if (string === null || string === undefined) {
        return "";
    }
    return Buffer.from(string.trim(), "utf-8")
        .toString()
        .replace(/[^\x20-\x7E]/g, "");
};
export function getDomainFromUrl(u) {
    const url = new URL(u);
    if (url.hostname.startsWith("www.")) {
        return url.hostname.replace("www.", "");
    }
    return url.hostname;
}
export function hasBeenMoreThanXMinutes(timestamp, xMinutes) {
    const timestampMs = timestamp.getTime();
    const currentTimeMs = new Date().getTime();
    const differenceMs = currentTimeMs - timestampMs;
    const xMinutesMs = xMinutes * 60 * 1000;
    return differenceMs > xMinutesMs;
}
export const getPlatform = (platformName) => {
    const platform = PLATFORM_NAME_TO_ENUM[platformName.trim().toUpperCase()];
    if (platform)
        return platform;
    throw new Error(`invalid platform name: ${platformName}`);
};
export const getPlatformName = (platform) => {
    return validateAndLowerCase(platform);
};
export const isSameToken = (tokenA, tokenB) => {
    return (tokenA &&
        tokenB &&
        validateAndFormatAddress(tokenA?.address) == validateAndFormatAddress(tokenB?.address) &&
        tokenA?.platformId == tokenB?.platformId);
};
export const isOnSamePlatform = (tokenA, tokenB) => {
    return tokenA.platformId == tokenB.platformId;
};
export const isOnSameEnvironment = (tokenA, tokenB) => {
    return getPlatformEnvironment(tokenA.platformId) == getPlatformEnvironment(tokenB.platformId);
};
export const createTokenId = (token) => {
    return `${token.address}-${token.platformId}`;
};
export const isNativeToken = (token) => {
    return token?.address == NATIVE_TOKEN_ADDRESS;
};
export const isNativeTokenETH = (token) => {
    const platform = getPlatformFromPlatformId(token.platformId);
    if (platform && PLATFORM_CONFIGS[platform]) {
        return isNativeToken(token) && PLATFORM_CONFIGS[platform].nativeTokenCoingeckoId == "ethereum";
    }
    return false;
};
export const createNativeFungibleTokenAmount = (value, platformId) => {
    const platform = getPlatformFromPlatformId(platformId);
    const config = platform ? PLATFORM_CONFIGS[platform] : undefined;
    if (!config) {
        return undefined;
    }
    const { nativeCurrency, nativeTokenCoingeckoId } = config;
    const fungibleToken = new FungibleToken(platformId, NATIVE_TOKEN_ADDRESS, nativeCurrency.decimals, nativeCurrency.symbol, nativeCurrency.name, undefined, false, true, nativeTokenCoingeckoId);
    return FungibleTokenAmount.fromRawAmount(fungibleToken, value);
};
export const createNativeCurrencyAmount = (value, platformId) => {
    const currency = getNativeCurrency(platformId);
    if (!currency)
        return undefined;
    return CurrencyAmount.fromRawAmount(currency, value);
};
export const getNativeCurrency = (platformId) => {
    const platform = getPlatformFromPlatformId(platformId);
    const config = platform ? PLATFORM_CONFIGS[platform] : undefined;
    if (!config) {
        return undefined;
    }
    const { nativeCurrency, nativeTokenCoingeckoId } = config;
    return new Currency(nativeCurrency.decimals, nativeCurrency.symbol, nativeCurrency.name, undefined, false, false, nativeTokenCoingeckoId);
};
export const getNativeFungibleToken = (platformId) => {
    const currency = getNativeCurrency(platformId);
    if (!currency)
        return undefined;
    return new FungibleToken(platformId, NATIVE_TOKEN_ADDRESS, currency.decimals, currency.symbol, currency.name, currency.logoUrl, false, true, currency.coinGeckoId);
};
export const getNowInEasternTime = () => {
    const now = new Date();
    return new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
};
